import React, { useCallback } from 'react';
import { StackActions } from '@react-navigation/native';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import useFetch from 'hooks/fetch';
import useFetchOld, { muteAll } from 'hooks/useFetch';
import useAppRating from 'hooks/useAppRating';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import OrderSendingView from '../components/OrderSendingView';
import RecurringBuyOrderConfirmView from '../components/RecurringBuyOrderConfirmView';
import { formatNextBuyDateDetailed } from '../utils';
import BalanceInfoText from '../components/BalanceInfoText';
import { createRecurringBuy, getRecurringBuys } from '../actions';
const strings = {
    failedToExecute: 'Failed to execute the order.',
};
const useRecurringOrderConfirm = (assetId) => {
    const rateApp = useAppRating();
    const navigation = useMainStackNavigation();
    const [fetch] = useFetch({ muteFetching: true });
    const [, , , fetchRecurringBuys] = useFetchOld(muteAll);
    const onSecondaryButtonPress = useCallback(() => {
        navigation.navigate('InvestSelectAccountToTopup', { flow: 'recurringOrder' });
        return true;
    }, [navigation]);
    const executeCallback = useCallback(async ({ pin, amount, assetId, buyConfig, nonTradingDayPreference }) => {
        const content = <OrderSendingView />;
        Modal.hide();
        Modal.show(content, {
            top: true,
            fullscreen: true,
            nonDismissable: true,
            minShowTime: 1500,
        });
        try {
            const response = await fetch(createRecurringBuy({
                pin,
                assetId,
                buyConfig,
                amount: amount.value,
                nonTradingDayPreference,
            }));
            fetchRecurringBuys(getRecurringBuys());
            fetchRecurringBuys(getRecurringBuys(assetId));
            const { nextBuy } = response.recurringBuy;
            const nextBuyReadableDate = formatNextBuyDateDetailed(nextBuy);
            Modal.hide(() => {
                navigation.navigateDeprecated('Success', {
                    onSecondaryButtonPress,
                    secondaryButtonText: 'Deposit',
                    title: 'Recurring order created',
                    subTitle: (<Text TextThin-16 Secondary>
                  Your recurring order has been created and it will next execute on{' '}
                  <Text Text-16 Secondary>
                    {nextBuyReadableDate}
                  </Text>
                  . Make sure you always have enough money in your available balance.
                </Text>),
                    buttonText: 'Done',
                    BtnPrefixComponent: BalanceInfoText,
                    onContinue: () => {
                        const { routes } = navigation.getState();
                        const hasInvestScreenInRoutes = routes.find((route) => route.name === 'InvestStock');
                        if (!hasInvestScreenInRoutes) {
                            const { key } = routes[1];
                            const resetAction = StackActions.replace('InvestStock', {
                                assetId,
                            });
                            navigation.dispatch({
                                ...resetAction,
                                source: key,
                                target: navigation.getState().key,
                            });
                        }
                        navigation.navigateDeprecated('InvestStock', {
                            assetId,
                        });
                        rateApp();
                        return true;
                    },
                });
            }, { top: true });
        }
        catch (error) {
            const axiosErr = error;
            let message = strings.failedToExecute;
            if (axiosErr?.response?.data?.user_message) {
                message = axiosErr.response.data.user_message;
            }
            else if ('message' in error && error.message) {
                message = error.message;
            }
            // failed to execute an order
            if (error?.errorType === 'PinError') {
                Modal.hide(undefined, { top: true });
                throw new Error('Refresh token expired');
            }
            else {
                Modal.hide(() => {
                    Modal.showError(message);
                }, { top: true });
            }
        }
    }, [fetchRecurringBuys, onSecondaryButtonPress, rateApp]);
    const confirmCallback = useCallback((amount, buyConfig, nonTradingDayPreference, fxConversion, totalAmount) => {
        const content = (<RecurringBuyOrderConfirmView assetId={assetId} amount={{
                value: amount,
                currency: 'GBP',
            }} buyConfig={buyConfig} fxConversion={fxConversion} execute={executeCallback} totalAmount={totalAmount} nonTradingDayPreference={nonTradingDayPreference}/>);
        Modal.show(content);
    }, [assetId, executeCallback]);
    return [confirmCallback];
};
export default useRecurringOrderConfirm;
